import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { affordableFrozenPrefix, frozenOffsets as computeFrozenOffsets, minTableWidth, resolveColumn, resolveColumns, stretchColumns } from '../columns/columnsState';
import { ACTIONS_COLUMN_WIDTH, CHECKBOX_GUTTER_WIDTH, END_EDGE_GUTTER_WIDTH, STRETCH_SAFETY_MARGIN, TABLE_FRAME_WIDTH } from '../table.constants';
export function useTableLayout(param) {
    let { mainColumn, columns, trailingColumn, columnsState, floors, hasActions = false, hasSelection = false, reservedWidth = 0, padsEndEdge = true, framed = false } = param;
    const scrollerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [isScrolledX, setIsScrolledX] = useState(false);
    const [isScrolledY, setIsScrolledY] = useState(false);
    const [hasColumnsBeyondEnd, setHasColumnsBeyondEnd] = useState(false);
    const [hasRowsBelow, setHasRowsBelow] = useState(false);
    const declared = useMemo(()=>resolveColumns({
            mainColumn,
            columns,
            columnsState,
            floors
        }), [
        mainColumn,
        columns,
        columnsState,
        floors
    ]);
    const resolvedTrailingColumn = useMemo(()=>trailingColumn == null ? undefined : resolveColumn({
            column: trailingColumn,
            isMain: false,
            columnsState,
            floors
        }), [
        trailingColumn,
        columnsState,
        floors
    ]);
    const hasEndEdge = padsEndEdge && !hasActions && resolvedTrailingColumn == null;
    // WHY: the safety margin is slack that counts toward the end gutter.
    const endEdgeReserve = hasEndEdge ? END_EDGE_GUTTER_WIDTH - STRETCH_SAFETY_MARGIN : 0;
    const endEdgeWidth = hasEndEdge ? END_EDGE_GUTTER_WIDTH : 0;
    const frameWidth = framed ? TABLE_FRAME_WIDTH : 0;
    const resolved = useMemo(()=>{
        var _ref;
        return stretchColumns({
            resolved: declared,
            availableWidth: containerWidth - frameWidth - STRETCH_SAFETY_MARGIN - reservedWidth - ((_ref = resolvedTrailingColumn === null || resolvedTrailingColumn === void 0 ? void 0 : resolvedTrailingColumn.width) !== null && _ref !== void 0 ? _ref : 0) - (hasSelection ? CHECKBOX_GUTTER_WIDTH : 0) - (hasActions ? ACTIONS_COLUMN_WIDTH : 0) - endEdgeReserve
        });
    }, [
        declared,
        containerWidth,
        hasActions,
        hasSelection,
        reservedWidth,
        resolvedTrailingColumn,
        endEdgeReserve,
        frameWidth
    ]);
    const checkboxWidth = hasSelection ? CHECKBOX_GUTTER_WIDTH : 0;
    const frozenPrefixLength = useMemo(()=>affordableFrozenPrefix({
            resolved,
            checkboxWidth,
            containerWidth
        }), [
        resolved,
        checkboxWidth,
        containerWidth
    ]);
    const frozenOffsets = useMemo(()=>computeFrozenOffsets({
            resolved,
            checkboxWidth
        }), [
        resolved,
        checkboxWidth
    ]);
    const minWidth = useMemo(()=>{
        var _ref;
        return minTableWidth({
            resolved,
            checkboxWidth
        }) + ((_ref = resolvedTrailingColumn === null || resolvedTrailingColumn === void 0 ? void 0 : resolvedTrailingColumn.width) !== null && _ref !== void 0 ? _ref : 0) + endEdgeWidth;
    }, [
        resolved,
        checkboxWidth,
        resolvedTrailingColumn,
        endEdgeWidth
    ]);
    const readScroller = (scroller)=>{
        setContainerWidth(scroller.clientWidth);
        setContainerHeight(scroller.clientHeight);
        setIsScrolledX(scroller.scrollLeft > 0);
        setIsScrolledY(scroller.scrollTop > 0);
        setHasColumnsBeyondEnd(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
        setHasRowsBelow(scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1);
    };
    useLayoutEffect(()=>{
        const scroller = scrollerRef.current;
        if (scroller == null) return undefined;
        const measure = ()=>readScroller(scroller);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(scroller);
        // WHY: rows change the scroll height without resizing the scroller.
        if (scroller.firstElementChild != null) observer.observe(scroller.firstElementChild);
        return ()=>observer.disconnect();
    }, []);
    // WHY: widening a column changes the scroll width without resizing the scroller.
    useLayoutEffect(()=>{
        const scroller = scrollerRef.current;
        if (scroller != null) readScroller(scroller);
    }, [
        minWidth,
        resolved
    ]);
    return {
        scrollerRef,
        containerHeight,
        columns: resolved,
        trailingColumn: resolvedTrailingColumn,
        frozenOffsets,
        frozenPrefixLength,
        minWidth,
        isScrolledX,
        isScrolledY,
        hasColumnsBeyondEnd,
        hasRowsBelow,
        onScroll: ()=>scrollerRef.current != null && readScroller(scrollerRef.current)
    };
}
